import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

/* The client's button. Note height 36px and radius 0.6rem — the radius
   deliberately overrides the shadcn --radius default and is measured, not
   guessed. Padding is logical (px-*) so RTL needs no separate rule. */

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-hk text-sm font-medium transition-all cursor-pointer',
    'border border-transparent font-sans',
    'focus-visible:ring-3 focus-visible:ring-[color-mix(in_oklab,var(--hk-ring)_50%,transparent)]',
    'disabled:opacity-50 disabled:pointer-events-none',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-fg hover:bg-primary-hover',
        outline: 'bg-card border-primary text-primary hover:bg-secondary',
        subtle:
          'bg-transparent text-primary border-[color-mix(in_oklab,var(--hk-primary)_30%,transparent)] ' +
          'hover:bg-[color-mix(in_oklab,var(--hk-primary)_10%,transparent)]',
        ghost: 'bg-transparent text-primary hover:bg-secondary',
        destructive: 'bg-destructive text-destructive-fg hover:brightness-95',
      },
      size: {
        sm: 'h-control-sm px-3 gap-1.5 text-sm',
        md: 'h-control px-4',
        lg: 'h-control-lg px-6',
        /* Mobile. 44px is the gloved-hand minimum from the Screen Inventory
           cross-cutting rules and is a floor, not a preference. */
        touch: 'h-touch px-5 w-full text-[15px]',
        icon: 'h-control w-control p-0',
        'icon-sm': 'h-control-sm w-control-sm p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />
}

export { buttonVariants }
